import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Timeline, {
  TimelineHeaders,
  SidebarHeader,
  DateHeader,
} from "react-calendar-timeline";
import "react-calendar-timeline/lib/Timeline.css";
import dayjsTZ from "../../../../utils/dayjs";
import { colorForRequest } from "../utils/statusColors";

const SIDEBAR_WIDTH = 180;

const PlanerGantt = ({
  groups = [],
  leave = [],
  rangeStart,
  rangeEnd,
  onItemClick,
}) => {
  const wrapRef = useRef(null);
  const [canvasWidth, setCanvasWidth] = useState(0);
  const [items, setItems] = useState([]);

  const timeStart = useMemo(
    () => dayjsTZ(rangeStart || undefined).startOf("month").valueOf(),
    [rangeStart]
  );
  const timeEnd = useMemo(
    () =>
      dayjsTZ(rangeEnd || rangeStart || undefined)
        .endOf("month")
        .valueOf(),
    [rangeStart, rangeEnd]
  );

  const [visible, setVisible] = useState(() => ({
    start: timeStart,
    end: timeEnd,
  }));

  useEffect(() => {
    setVisible({ start: timeStart, end: timeEnd });
  }, [timeStart, timeEnd]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      setCanvasWidth(el?.clientWidth || 0);
      return undefined;
    }
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width || 0;
      setCanvasWidth(Math.max(0, Math.floor(w)));
    });
    ro.observe(el);
    setCanvasWidth(Math.max(0, Math.floor(el.clientWidth)));
    return () => ro.disconnect();
  }, []);

  const percentage = useMemo(() => {
    if (!groups.length) return 0;
    const now = dayjsTZ().valueOf();
    const onVac = new Set(
      (leave || [])
        .filter(
          (l) =>
            l?.requestStatus === "Aprovuar" &&
            l?.requestPeriod?.[0] <= now &&
            l?.requestPeriod?.at?.(-1) >= now
        )
        .map((l) => l.userSub)
    );
    const count = groups.filter((g) => onVac.has(g.id)).length;
    return Math.round((count * 1000) / groups.length) / 10;
  }, [groups, leave]);

  useEffect(() => {
    const byGroup = new Set(groups.map((g) => g.id));
    setItems(
      (leave || [])
        .filter((lev) => byGroup.has(lev?.userSub) && lev?.requestPeriod?.length)
        .map((lev) => ({
          id: lev.requestId,
          group: lev.userSub,
          title: `${dayjsTZ(lev.requestPeriod[0]).format("D")} – ${dayjsTZ(
            lev.requestPeriod.at(-1)
          ).format("D")}`,
          start_time: dayjsTZ(lev.requestPeriod[0]).startOf("day").valueOf(),
          end_time:
            lev.requestType === "Dite Personale"
              ? dayjsTZ(lev.requestPeriod[0]).endOf("day").valueOf()
              : dayjsTZ(lev.requestPeriod.at(-1)).endOf("day").valueOf(),
          itemProps: {
            style: {
              background: colorForRequest(lev),
              color: "#FFFFFF",
              minWidth: "48px",
              cursor: "pointer",
            },
          },
        }))
    );
  }, [leave, groups]);

  const onTimeChange = useCallback(
    (start, end, updateScrollCanvas) => {
      const span = end - start;
      let nextStart = start;
      let nextEnd = end;
      // Keep pan/zoom inside the selected month so the canvas cannot grow the page.
      if (nextStart < timeStart) {
        nextStart = timeStart;
        nextEnd = timeStart + span;
      }
      if (nextEnd > timeEnd) {
        nextEnd = timeEnd;
        nextStart = timeEnd - span;
      }
      if (nextStart < timeStart) {
        nextStart = timeStart;
        nextEnd = timeEnd;
      }
      setVisible({ start: nextStart, end: nextEnd });
      updateScrollCanvas(nextStart, nextEnd);
    },
    [timeStart, timeEnd]
  );

  if (!groups.length) {
    return (
      <div className="planer-gantt-empty">
        Nuk ka punonjës për filtrat e zgjedhur.
      </div>
    );
  }

  const timelineWidth = canvasWidth > 0 ? canvasWidth : undefined;

  return (
    <div className="planer-gantt" ref={wrapRef}>
      {timelineWidth ? (
        <Timeline
          key={`${timeStart}-${timelineWidth}`}
          style={{ width: timelineWidth }}
          itemHeightRatio={0.75}
          lineHeight={40}
          sidebarWidth={SIDEBAR_WIDTH}
          groups={groups}
          items={items}
          visibleTimeStart={visible.start}
          visibleTimeEnd={visible.end}
          onTimeChange={onTimeChange}
          canMove={false}
          canResize={false}
          canChangeGroup={false}
          stackItems
          minZoom={24 * 60 * 60 * 1000}
          maxZoom={timeEnd - timeStart}
          onItemSelect={(itemId) => {
            const req = leave.find((l) => l.requestId === itemId);
            if (req && onItemClick) onItemClick(req);
          }}
          onItemClick={(itemId) => {
            const req = leave.find((l) => l.requestId === itemId);
            if (req && onItemClick) onItemClick(req);
          }}
        >
          <TimelineHeaders>
            <SidebarHeader>
              {({ getRootProps }) => (
                <div {...getRootProps()} className="planer-gantt-sidebar-head">
                  <span className="planer-gantt-pct">{percentage}% pushim</span>
                </div>
              )}
            </SidebarHeader>
            <DateHeader unit="primaryHeader" />
            <DateHeader />
          </TimelineHeaders>
        </Timeline>
      ) : null}
    </div>
  );
};

export default PlanerGantt;
